import { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, Divider, ListItem } from '@mui/material';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { ListCell, ListHeader, ListRow, ViewNotesModal } from '/src/components';
import { useAdjustmentStore } from '/src/context';
import { mediumDate } from '/src/lib/constants';
import { Adjustment, Note } from '/src/lib/models';
import { TListAdjustmentsRequest, Totals } from '/src/lib/types';
import * as utils from '/src/utils';

export type AdjustmentsListProps = {
  /**
   * List of Adjustments to display
   */
  data?: Adjustment[];
  /**
   * Query to include when fetching Adjustments
   */
  query?: TListAdjustmentsRequest;
};

/**
 * Adjustments List
 */
export const AdjustmentsList: React.FC<AdjustmentsListProps> = observer(({ data, query }) => {
  const defaultSortType = 'sequenceNumber';
  const defaultSortReverse = false;
  const rowHeight = 48;

  /** Context **/
  const adjustmentStore = useAdjustmentStore();

  /** State **/
  const [adjustments, setAdjustments] = useState<Adjustment[]>([]);
  const [totals, setTotals] = useState<Totals>({});
  const [resolved, setResolved] = useState(false);
  const [sortType, setSortType] = useState(defaultSortType);
  const [sortReverse, setSortReverse] = useState(defaultSortReverse);

  // View Notes popup shows if `notes` is defined
  const [notes, setNotes] = useState<Note[]>();
  const [notesName, setNotesName] = useState<string>();

  /** Computed **/

  const listData = useMemo(() => {
    if (adjustments) {
      const list = adjustments.toSorted((a, b) => ((a as any)[sortType] > (b as any)[sortType] ? 1 : -1));
      if (sortReverse) list.reverse();

      return list;
    } else return [];
  }, [adjustments, sortType, sortReverse]);

  /** Methods **/

  const formatData = (data: Adjustment[]) => {
    // Prepare totals
    const totals: Totals = {};
    totals.assetValue = 0;
    totals.amount = 0;
    totals.resultingAssetValue = 0;

    const adjustments = data.map((adjustment) => {
      // Calculate Adjustment values
      if (adjustment.assetValue) adjustment.resultingAssetValue = adjustment.assetValue + adjustment.amount;

      // Calculate totals
      if (adjustment.assetValue) totals.assetValue += adjustment.assetValue;
      totals.amount += adjustment.amount;
      if (adjustment.resultingAssetValue) totals.resultingAssetValue += adjustment.resultingAssetValue;

      // Store
      return adjustment;
    });

    // Save data
    setAdjustments(adjustments);
    setTotals(totals);

    // Set as resolved
    setResolved(true);
  };

  const showNotesPopup = (notes?: Note[], name?: string) => {
    setNotes(notes ?? []);
    setNotesName(name);
  };

  /**
   * Sets sortType and sortReverse based on given sortType
   * @param type string sortType to use
   */
  const sortList = (type?: string) => {
    if (type) {
      if (sortType === type) setSortReverse(!sortReverse);
      else setSortType(type);
    }
  };

  /** Effects **/

  useEffect(() => {
    if (data) {
      formatData(data);
    } else if (query && Object.values(query).length) {
      adjustmentStore.query(query).then((adjustments) => adjustments && formatData(adjustments));
    }
  }, [data, query]);

  /** Render **/
  return (
    <Box>
      {/* HEADER */}
      {!!listData.length && (
        <ListHeader
          items={[
            { flex: 5, propName: 'notes', label: 'Notes' },
            { flex: 1, propName: 'name', label: 'Name' },
            { flex: 10, propName: 'effectiveDate', label: 'Effective Date' },
            { flex: 1, propName: 'assetValue', label: 'Starting Asset Value', textAlign: 'right' },
            { flex: 1, propName: 'amount', label: 'Amount', textAlign: 'right' },
            { flex: 1, propName: 'resultingAssetValue', label: 'Resulting Asset Value', textAlign: 'right' },
          ]}
          sortType={sortType}
          sortReverse={sortReverse}
          sortList={sortList}
        />
      )}

      {!!listData.length && <Divider />}

      {/* LIST */}
      {resolved && !!listData.length && (
        <Box component="md-content" height={listData.length * rowHeight + 'px'} maxHeight={9 * rowHeight + 'px'}>
          {listData.map((adjustment, i) => (
            <ListRow
              key={adjustment._id}
              rowHeight={rowHeight}
              padForScrollbar={listData.length > 8}
              items={[
                {
                  propName: 'notes',
                  isNotes: true,
                  flex: 5,
                  hide: !adjustment.notes.length,
                  showNotesPopup: () => showNotesPopup(adjustment.notes, adjustment.name),
                },
                {
                  flex: 1,
                  propName: 'name',
                  label: 'Name',
                  content: adjustment.name,
                  url: `/adjustments/${adjustment._id}`,
                },
                {
                  flex: 10,
                  propName: 'effectiveDate',
                  label: 'Effective Date',
                  content: dayjs(adjustment.effectiveDate).format(mediumDate),
                },
                {
                  flex: 1,
                  propName: 'assetValue',
                  label: 'Starting Asset Value',
                  content: utils.currency.format(adjustment.assetValue),
                  textAlign: 'right',
                },
                {
                  flex: 1,
                  propName: 'amount',
                  label: 'Amount',
                  content: utils.currency.format(adjustment.amount),
                  textAlign: 'right',
                },
                {
                  flex: 1,
                  propName: 'resultingAssetValue',
                  label: 'Resulting Asset Value',
                  content: utils.currency.format(adjustment.resultingAssetValue),
                  textAlign: 'right',
                },
              ]}
              rowIndex={i}
            />
          ))}
        </Box>
      )}

      <Divider />

      {/* TOTALS */}
      {!!listData.length && (
        <ListItem sx={{ height: rowHeight }}>
          <Box
            display="flex"
            flex="100"
            justifyContent="space-between"
            alignItems="center"
            className={`medium md-subhead-sm text-center ${listData.length > 8 && 'padForScrollbar'}`}
          >
            {[
              { flex: 5, propName: 'notes', label: 'Totals', content: 'Totals' },
              { propName: 'name', label: 'Name' },
              { flex: 10, propName: 'effectiveDate', label: 'Effective Date' },
              {
                propName: 'assetValue',
                label: 'Starting Asset Value',
                textAlign: 'right',
                content: utils.currency.format(totals.assetValue),
              },
              {
                propName: 'amount',
                label: 'Amount',
                textAlign: 'right',
                content: utils.currency.format(totals.amount),
              },
              {
                propName: 'resultingAssetValue',
                label: 'Resulting Asset Value',
                textAlign: 'right',
                content: utils.currency.format(totals.resultingAssetValue),
              },
            ].map(({ propName, ...itemProps }, i) => (
              <ListCell
                key={`${propName}-${i}`}
                content={i === 0 ? 'Totals' : totals[propName] ? utils.currency.format(totals[propName]) : ''}
                {...itemProps}
              />
            ))}
          </Box>
        </ListItem>
      )}

      {resolved && !listData.length && (
        <ListItem>
          <Box display="flex" flex="100" justifyContent="center" alignItems="center" className="pad-top-bottom">
            <Box>No Adjustments found.</Box>
          </Box>
        </ListItem>
      )}

      {!resolved && (
        <ListItem>
          <Box display="flex" flex="100" justifyContent="center" alignItems="center" className="pad-top-bottom">
            <CircularProgress className="md-primary" md-mode="indeterminate" />
          </Box>
        </ListItem>
      )}

      {notes && (
        <ViewNotesModal
          notes={notes}
          name={notesName}
          open={!!notes}
          setOpen={(open) => !open && setNotes(undefined)}
        />
      )}
    </Box>
  );
});
